declare let gtag: Function;

import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subscription, forkJoin, of } from 'rxjs';
import { takeWhile } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { DossierService, DownstreamExportResult } from '../../services/dossier.service';
import { AuthService } from '../../services/auth.service';
import { CryptoService } from '../../services/crypto.service';
import { Document, Page, PageStatus, UploadStatus, DocumentStatus, ExtractionResult, ModelTier } from '../../models/dossier.model';
import { extractApiError } from '../../utils/api-error.utils';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-upload-documents',
  templateUrl: './upload-documents.component.html',
  styleUrls: ['./upload-documents.component.css']
})
export class UploadDocumentsComponent implements OnInit, OnDestroy {

  dossierId = '';
  uploadId = '';
  dossierName = '';
  uploadNumber = 0;

  documents: Document[] = [];
  documentPages: Record<string, Page[]> = {};
  pageImageUrls: Record<string, string> = {};

  /** Whether the parent dossier requires DEK and it's not yet unlocked. */
  dekWarning = false;

  /** Whether the parent dossier is in batch mode (disables page reorganization). */
  batchMode = false;

  /** Per-document schema source: 'internal' or 'external'. Keyed by doc oid. */
  docSchemaSource: Record<string, 'internal' | 'external'> = {};

  /** Document type lists fetched from /document_types endpoint. */
  internalDocumentTypes: string[] = [];
  externalDocumentTypes: string[] = [];

  /** Per-document extraction status and tokens. */
  docExtractionStatus: Record<string, string> = {};
  docExtractionTokens: Record<string, number | null> = {};
  docExtractionModel: Record<string, string | null> = {};

  /** Per-document downstream send state. */
  exportingDocId: string | null = null;
  exportDocMsg: Record<string, string> = {};
  downstreamSystemName: string | null = null;

  /** Per-document extraction polling subscriptions. */
  private docPollSubs: Record<string, Subscription> = {};

  uploadStatus: UploadStatus | '' = '';
  loading = false;
  error = '';
  extracting = false;

  /** Extraction configuration — backend + model selection */
  availableModels: string[] = environment.modelOptions;
  extractBackend: '' | 'openai' | 'ollama' | 'bedrock' | 'local' = '';
  extractModel = '';

  /** Output format for extraction (json/xml/csv). Honoured by batch extraction
   *  and passed to per-document re-extraction, so it can be chosen before
   *  extracting rather than requiring a re-extract afterwards. */
  outputFormat = 'json';
  readonly outputFormats = ['json', 'xml', 'csv'];

  /** Tracks whether the dossier's batch_output_format has been loaded (to avoid overwriting user edits). */
  private batchOutputFormatLoaded = false;

  /** Model tiers for non-developer users */
  modelTiers: ModelTier[] = [];

  /** Tracks whether the dossier's batch_model has been loaded (to avoid overwriting user edits). */
  private batchModelLoaded = false;

  /** Per-document selection checkboxes for selective re-extraction */
  selectedDocs: Set<string> = new Set();

  /** Whether we've already auto-selected all docs after classification. */
  private didAutoSelect = false;

  /** Side panel viewer state. */
  viewerPage: Page | null = null;
  viewerImageUrl: string | null = null;
  viewerPanelWidth = 400;
  private resizing = false;
  private resizeStartX = 0;
  private resizeStartWidth = 0;

  /** Drag-and-drop state for thumbnail reordering. */
  dragSource: { docOid: string; pageOid: string } | null = null;
  dragOverTarget: { docOid: string; pageOid: string } | null = null;

  private pollTimer: ReturnType<typeof setInterval> | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private dossierService: DossierService,
    protected authService: AuthService,
    private cryptoService: CryptoService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.dossierId = this.route.snapshot.paramMap.get('id') || '';
    this.uploadId = this.route.snapshot.paramMap.get('uid') || '';
    this.loadDocumentTypes();
    this.loadConfig();
    this.loadDownstreamSystemName();
    if (this.dossierId && this.uploadId) {
      this.loadDocuments();
    }
  }

  private loadDownstreamSystemName(): void {
    this.dossierService.getDownstreamEndpoint().subscribe({
      next: (res) => { this.downstreamSystemName = res.system_name; },
      error: () => {},
    });
  }

  ngOnDestroy(): void {
    this.stopPoll();
    this.stopAllDocPolling();
    this.revokeAllImageUrls();
  }

  // --- Data loading ---

  private loadDocumentTypes(): void {
    this.dossierService.getDocumentTypes().subscribe({
      next: (res) => {
        if (res.internal?.length) { this.internalDocumentTypes = res.internal; }
        if (res.external?.length) { this.externalDocumentTypes = res.external; }
      }
    });
  }

  private loadConfig(): void {
    this.dossierService.getConfig().subscribe({
      next: (res: any) => {
        const defaults = res?.defaults;
        if (defaults?.model && !this.extractModel) {
          this.extractModel = defaults.model;
        }
        if (defaults?.extract_backend && !this.extractBackend) {
          this.extractBackend = defaults.extract_backend;
        }
        const tiers = res?.model_tiers;
        if (Array.isArray(tiers) && tiers.length > 0) {
          this.modelTiers = tiers;
          // If no model selected yet, default to standard tier
          if (!this.extractModel) {
            const std = tiers.find((t: any) => t.key === 'standard');
            if (std) this.extractModel = std.model;
          }
        }
      },
    });
  }

  loadDocuments(): void {
    this.loading = true;
    this.error = '';
    this.dossierService.getDocuments(this.dossierId, this.uploadId).subscribe({
      next: (docs) => {
        this.revokeAllImageUrls();
        this.documents = docs;
        // Initialize schema source per document from stored document_type
        for (const doc of docs) {
          if (doc.document_type?.startsWith('xsd/')) {
            this.docSchemaSource[doc.oid] = 'external';
          }
        }
        this.loading = false;
        this.resolveUploadStatus();
        this.loadAllPages();
        this.syncExtractionStatuses();
        this.maybeAutoSelectAll();
      },
      error: (err) => {
        this.error = err?.error?.detail || err?.message || 'Failed to load documents';
        this.trackError('load_documents', this.error);
        this.loading = false;
      }
    });
  }

  private resolveUploadStatus(): void {
    this.dossierService.getDossier(this.dossierId).subscribe({
      next: (dossier) => {
        this.batchMode = !!dossier.batch_mode;
        this.dossierName = dossier.external_id || '';
        const uploads = dossier.uploads || [];
        const uploadIdx = uploads.findIndex(u => u.oid === this.uploadId);
        this.uploadNumber = uploadIdx >= 0 ? uploadIdx + 1 : 0;
        const upload = uploads[uploadIdx];
        if (upload) {
          this.uploadStatus = upload.status;
          this.managePoll();
        }
        // Pre-populate extraction model selectors from dossier's batch_model
        if (!this.batchModelLoaded && dossier.batch_model) {
          this.batchModelLoaded = true;
          this.extractModel = dossier.batch_model;
          if (dossier.batch_model.startsWith('bedrock/')) {
            this.extractBackend = 'bedrock';
          } else if (dossier.batch_model.startsWith('ollama/')) {
            this.extractBackend = 'ollama';
          } else if (dossier.batch_model.startsWith('local/')) {
            this.extractBackend = 'local';
          } else {
            this.extractBackend = 'openai';
          }
        }
        // Pre-populate output format selector from dossier's batch_output_format
        if (!this.batchOutputFormatLoaded && dossier.batch_output_format) {
          this.batchOutputFormatLoaded = true;
          this.outputFormat = dossier.batch_output_format;
        }
        // DEK awareness: check if dossier requires unlock
        this.checkDossierDekStatus(dossier);
      }
    });
  }

  /**
   * Check DEK status for the parent dossier. If encrypted and not yet
   * unlocked, attempt auto-unlock with cached key or show warning.
   */
  private checkDossierDekStatus(dossier: any): void {
    if (!dossier.encrypted_dek) {
      this.dekWarning = false;
      return;
    }

    // Already unlocked this session?
    if (this.cryptoService.isDossierUnlocked(dossier.oid)) {
      this.dekWarning = false;
      return;
    }

    // Cached private key? Auto-unlock transparently
    if (this.cryptoService.hasCachedPrivateKey()) {
      this.dekWarning = false;
      this.autoUnlockDossierDek(dossier);
      return;
    }

    // No cached key — show warning directing user back to dossier detail
    this.dekWarning = true;
  }

  /**
   * Auto-unlock DEK using cached private key (same pattern as DossierDetailComponent).
   */
  private async autoUnlockDossierDek(dossier: any): Promise<void> {
    const privateKey = this.cryptoService.getCachedPrivateKey();
    if (!privateKey || !dossier.encrypted_dek) { return; }

    try {
      const dekHex = await this.cryptoService.unwrapDek(privateKey, dossier.encrypted_dek);
      await this.authService.unlockDek(dossier.oid, dekHex).toPromise();
      this.cryptoService.addUnlockedDossier(dossier.oid);
      this.dekWarning = false;
    } catch {
      // Auto-unlock failed — show warning
      this.dekWarning = true;
    }
  }

  /** Start or stop polling based on upload status. */
  private managePoll(): void {
    if (this.isProcessing && !this.pollTimer) {
      this.pollTimer = setInterval(() => this.pollUploadStatus(), 5000);
    } else if (!this.isProcessing) {
      this.stopPoll();
    }
  }

  private pollUploadStatus(): void {
    this.dossierService.getDossier(this.dossierId).subscribe({
      next: (dossier) => {
        const upload = dossier.uploads?.find(u => u.oid === this.uploadId);
        if (upload) {
          this.uploadStatus = upload.status;
          if (this.isClassificationCompleted) {
            this.stopPoll();
            this.loadDocuments();
          } else if (this.isFailedStatus) {
            this.stopPoll();
          }
        }
      }
    });
  }

  private stopPoll(): void {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  private loadAllPages(): void {
    this.documentPages = {};
    for (const doc of this.documents) {
      if (doc.pages?.length) {
        this.documentPages[doc.oid] = doc.pages;
        for (const page of doc.pages) { this.loadPageImage(page); }
      }
      // Initialise schema source from document_type prefix
      if (!this.docSchemaSource[doc.oid]) {
        this.docSchemaSource[doc.oid] = doc.document_type?.startsWith('xsd/') ? 'external' : 'internal';
      }
    }
  }

  private loadPageImage(page: Page): void {
    if (this.pageImageUrls[page.oid]) { return; }
    this.dossierService.getPageImage(this.dossierId, this.uploadId, page.oid).subscribe({
      next: (blob) => { this.pageImageUrls[page.oid] = URL.createObjectURL(blob); },
      error: (err) => {
        if (err?.status === 400) {
          this.dekWarning = true;
        }
      }
    });
  }

  private revokeAllImageUrls(): void {
    Object.values(this.pageImageUrls).forEach(url => URL.revokeObjectURL(url));
    this.pageImageUrls = {};
  }

  // --- Per-document extraction status tracking ---

  /** Sync extraction statuses from document records and start polling for in-progress ones. */
  private syncExtractionStatuses(): void {
    for (const doc of this.documents) {
      this.docExtractionStatus[doc.oid] = doc.status;
      this.docExtractionTokens[doc.oid] = this.getExtractionTokens(doc);
      this.docExtractionModel[doc.oid] = this.getExtractionModel(doc);

      // Start polling for documents that are currently extracting
      if (doc.status === DocumentStatus.EXTRACTING && !this.docPollSubs[doc.oid]) {
        this.startDocExtractionPolling(doc.oid);
      }
    }
  }

  private startDocExtractionPolling(docOid: string): void {
    // Stop existing poll if any
    this.stopDocPolling(docOid);

    this.docPollSubs[docOid] = this.dossierService.pollExtractionStatus(
      this.dossierId, this.uploadId, docOid,
    ).pipe(
      takeWhile(result =>
        result.status !== DocumentStatus.EXTRACTED
        && result.status !== DocumentStatus.REVIEWED
        && result.status !== DocumentStatus.EXTRACTION_FAILED, true),
    ).subscribe({
      next: (result) => {
        this.docExtractionStatus[docOid] = result.status;
        if (result.status === DocumentStatus.EXTRACTED || result.status === DocumentStatus.REVIEWED) {
          this.stopDocPolling(docOid);
          // Update tokens from extraction result
          this.updateDocTokensFromResult(docOid, result);
          // Update the document in our local array
          this.refreshSingleDocument(docOid);
        }
        if (result.status === DocumentStatus.EXTRACTION_FAILED) {
          this.stopDocPolling(docOid);
          // Refresh to get error details from extraction_result JSONB
          this.refreshSingleDocument(docOid);
        }
      },
      error: () => {
        this.stopDocPolling(docOid);
      },
    });
  }

  private stopDocPolling(docOid: string): void {
    if (this.docPollSubs[docOid]) {
      this.docPollSubs[docOid].unsubscribe();
      delete this.docPollSubs[docOid];
    }
  }

  private stopAllDocPolling(): void {
    for (const oid of Object.keys(this.docPollSubs)) {
      this.docPollSubs[oid].unsubscribe();
    }
    this.docPollSubs = {};
  }

  private updateDocTokensFromResult(docOid: string, result: ExtractionResult): void {
    const er = result.extraction_result;
    if (er && typeof er === 'object' && 'billing_tokens' in (er as any)) {
      const bt = (er as any).billing_tokens;
      if (bt && typeof bt === 'object' && 'extraction' in bt) {
        this.docExtractionTokens[docOid] = bt.extraction;
      }
    }
    if (er && typeof er === 'object' && 'extraction_model' in (er as any)) {
      this.docExtractionModel[docOid] = (er as any).extraction_model;
    }
  }

  /** Refresh a single document from the API without full reload. */
  private refreshSingleDocument(docOid: string): void {
    this.dossierService.getDocuments(this.dossierId, this.uploadId).subscribe({
      next: (docs) => {
        const updated = docs.find(d => d.oid === docOid);
        if (updated) {
          const idx = this.documents.findIndex(d => d.oid === docOid);
          if (idx >= 0) {
            this.documents[idx] = updated;
            if (updated.pages?.length) {
              this.documentPages[docOid] = updated.pages;
              for (const page of updated.pages) { this.loadPageImage(page); }
            }
          }
        }
        // Check if all documents are now extracted → update upload status
        this.resolveUploadStatus();
      },
    });
  }

  /** Get extraction billing tokens from the summary field. */
  getExtractionTokens(doc: Document): number | null {
    return doc.extraction_tokens ?? null;
  }

  getExtractionModel(doc: Document): string | null {
    return doc.extraction_model ?? null;
  }

  getDocModel(doc: Document): string | null {
    return this.docExtractionModel[doc.oid] ?? null;
  }

  /** True when any document is currently extracting. */
  get anyExtracting(): boolean {
    return this.documents.some(d =>
      this.docExtractionStatus[d.oid] === DocumentStatus.EXTRACTING
    );
  }

  /** True only while extraction is actively running. */
  get operationsLocked(): boolean {
    return this.anyExtracting || this.extracting;
  }

  /** True when classification is done and no extraction is in progress — user can modify.
   *  In batch mode, page reorganization is permanently disabled. */
  get canModifyDocuments(): boolean {
    if (this.batchMode) { return false; }
    return this.isClassificationCompleted && !this.operationsLocked;
  }

  // --- Schema source & document type ---

  getSchemaSource(doc: Document): 'internal' | 'external' {
    return this.docSchemaSource[doc.oid] || 'internal';
  }

  onSchemaSourceChange(doc: Document, source: 'internal' | 'external'): void {
    this.docSchemaSource[doc.oid] = source;
    // Reset to first available type for the new source
    const types = this.getDocumentTypesForDoc(doc);
    if (types.length) {
      const currentBare = doc.document_type?.startsWith('xsd/') ? doc.document_type.substring(4) : doc.document_type;
      if (!types.includes(currentBare) && !types.includes(doc.document_type)) {
        this.onTypeChange(doc, source === 'external' ? 'xsd/' + types[0] : types[0]);
      }
    }
  }

  getDocumentTypesForDoc(doc: Document): string[] {
    return this.getSchemaSource(doc) === 'external'
      ? this.externalDocumentTypes
      : this.internalDocumentTypes;
  }

  /** Returns the bare type name (without xsd/ prefix) for display. */
  bareDocumentType(doc: Document): string {
    return doc.document_type?.startsWith('xsd/') ? doc.document_type.substring(4) : doc.document_type;
  }

  onTypeChange(doc: Document, newType: string): void {
    if (typeof gtag === 'function') {
      gtag('event', 'document_type_changed', { document_type: newType });
    }
    // Prefix with xsd/ for external types
    const effectiveType = this.getSchemaSource(doc) === 'external' ? 'xsd/' + newType : newType;
    this.dossierService.updateDocumentType(this.dossierId, this.uploadId, doc.oid, effectiveType).subscribe({
      next: (updated) => {
        doc.document_type = updated.document_type;
        doc.status = updated.status;
        this.docExtractionStatus[doc.oid] = updated.status;
        // Type change invalidates extraction — clear summary fields
        this.docExtractionTokens[doc.oid] = null;
        this.docExtractionModel[doc.oid] = null;
      },
      error: (err) => { this.error = err?.error?.detail || err?.message || 'Failed to update document type'; }
    });
  }

  // --- Extraction model selection ---

  /** Models filtered by the selected backend provider. */
  get filteredExtractModels(): string[] {
    switch (this.extractBackend) {
      case 'ollama':
        return this.availableModels.filter(m => m.startsWith('ollama/'));
      case 'bedrock':
        return this.availableModels.filter(m => m.startsWith('bedrock/'));
      case 'local':
        return this.availableModels.filter(m => m.startsWith('local/'));
      case 'openai':
        return this.availableModels.filter(m => !m.startsWith('ollama/') && !m.startsWith('bedrock/') && !m.startsWith('local/'));
      default:
        return this.availableModels;
    }
  }

  /** Strip provider prefix from model name for display. */
  displayModel(m: string): string {
    const slash = m.indexOf('/');
    return slash >= 0 ? m.substring(slash + 1) : m;
  }

  onExtractBackendChange(): void {
    const filtered = this.filteredExtractModels;
    if (filtered.length && !filtered.includes(this.extractModel)) {
      this.extractModel = filtered[0];
    } else if (!filtered.length) {
      this.extractModel = '';
    }
    this.persistBatchModel();
  }

  /** Persist the current extractModel as the dossier's batch_model. */
  onExtractModelChange(): void {
    this.persistBatchModel();
  }

  onTierModelChange(model: string): void {
    this.extractModel = model;
    this.persistBatchModel();
  }

  private persistBatchModel(): void {
    const model = this.extractModel.trim() || null;
    this.dossierService.updateBatchModel(this.dossierId, model).subscribe({
      error: (err) => {
        this.error = err?.error?.detail || err?.message || 'Failed to save model selection';
      },
    });
  }

  /** Persist the selected output format as the dossier's batch_output_format. */
  onOutputFormatChange(): void {
    this.dossierService.updateBatchOutputFormat(this.dossierId, this.outputFormat || null).subscribe({
      error: (err) => {
        this.error = err?.error?.detail || err?.message || 'Failed to save output format selection';
      },
    });
  }

  // --- Document selection (checkboxes) ---

  toggleDocSelection(doc: Document): void {
    if (this.selectedDocs.has(doc.oid)) {
      this.selectedDocs.delete(doc.oid);
    } else {
      this.selectedDocs.add(doc.oid);
    }
  }

  isDocSelected(doc: Document): boolean {
    return this.selectedDocs.has(doc.oid);
  }

  get hasSelection(): boolean {
    return this.selectedDocs.size > 0;
  }

  selectAll(): void {
    for (const doc of this.documents) {
      this.selectedDocs.add(doc.oid);
    }
  }

  /**
   * Auto-select all documents once, after classification completes, so the
   * user can extract immediately without manually ticking each checkbox.
   */
  private maybeAutoSelectAll(): void {
    if (this.didAutoSelect) { return; }
    if (!this.isClassificationCompleted) { return; }
    if (this.documents.length === 0) { return; }
    this.didAutoSelect = true;
    this.selectAll();
  }

  selectNone(): void {
    this.selectedDocs.clear();
  }

  selectAllFailed(): void {
    for (const doc of this.documents) {
      if (this.docExtractionStatus[doc.oid] === DocumentStatus.EXTRACTION_FAILED) {
        this.selectedDocs.add(doc.oid);
      }
    }
  }

  clearSelection(): void {
    this.selectedDocs.clear();
  }

  /** Download extraction results for selected documents. */
  downloadSelectedResults(): void {
    if (!this.hasSelection) {
      alert(this.getTranslation('uploadDocs.pleaseSelectDocument'));
      return;
    }
    const oids = Array.from(this.selectedDocs);
    this.dossierService.downloadResults(this.dossierId, this.uploadId, oids).subscribe({
      next: (resp) => {
        const blob = resp.body;
        if (!blob) return;

        // Extract filename from Content-Disposition header; fall back to .zip
        let filename = `results_${this.uploadId}.zip`;
        const disposition = resp.headers.get('Content-Disposition');
        if (disposition) {
          const match = disposition.match(/filename=([^\s;]+)/);
          if (match) {
            filename = match[1].replace(/^["']|["']$/g, '');
          }
        }

        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
        if (typeof gtag === 'function') {
          gtag('event', 'download_results', { document_count: oids.length });
        }
      },
      error: (err) => {
        this.error = err?.error?.detail || err?.message || 'Failed to download results';
        this.trackError('download_results', this.error);
      },
    });
  }

  /** Download OCR text (.txt) for the selected documents, reflowed into lines. */
  downloadSelectedOcrText(): void {
    if (!this.hasSelection) {
      alert(this.getTranslation('uploadDocs.pleaseSelectDocument'));
      return;
    }
    const oids = Array.from(this.selectedDocs);
    this.buildAndDownloadOcrText(oids, `ocr_text_${this.uploadId}.txt`, 'dossier_bulk');
  }

  /** Download OCR text (.txt) for a single document. */
  downloadDocOcrText(doc: Document): void {
    const label = (this.bareDocumentType(doc) || doc.oid).replace(/[^\w.-]+/g, '_');
    this.buildAndDownloadOcrText([doc.oid], `ocr_text_${label}.txt`, 'dossier_single');
  }

  /** Download OCR text (.txt) for every document in the upload. */
  downloadAllOcrText(): void {
    const oids = this.documents.map(d => d.oid);
    this.buildAndDownloadOcrText(oids, `ocr_text_${this.uploadId}.txt`, 'dossier_all');
  }

  /** Shared core: fetch OCR for all pages of the given docs, reflow, bundle into one .txt. */
  private buildAndDownloadOcrText(oids: string[], filename: string, mode: string): void {
    // Flatten to (docOid, page) pairs across all requested documents.
    const tasks: { docOid: string; page: Page }[] = [];
    for (const docOid of oids) {
      for (const page of (this.documentPages[docOid] || [])) {
        tasks.push({ docOid, page });
      }
    }
    if (tasks.length === 0) return;

    const multiDoc = oids.length > 1;
    const requests = tasks.map(t =>
      this.dossierService.getPageOcr(this.dossierId, this.uploadId, t.page.oid)
    );
    forkJoin(requests).subscribe({
      next: (datas) => {
        const parts: string[] = [];
        let lastDoc: string | null = null;
        tasks.forEach((t, i) => {
          if (multiDoc && t.docOid !== lastDoc) {
            const doc = this.documents.find(d => d.oid === t.docOid);
            const label = doc ? (this.bareDocumentType(doc) || doc.oid) : t.docOid;
            parts.push(`===== Document: ${label} =====`);
            lastDoc = t.docOid;
          }
          parts.push(`--- Page ${t.page.page_number} ---`);
          parts.push(this.reconstructPageText(datas[i]));
          parts.push('');
        });
        const blob = new Blob([parts.join('\n')], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
        if (typeof gtag === 'function') {
          gtag('event', 'dd_ocr_download', { format: 'txt', mode, document_count: oids.length });
        }
      },
      error: (err) => {
        this.error = err?.error?.detail || err?.message || 'Failed to download OCR text';
        this.trackError('download_ocr_text', this.error);
      },
    });
  }

  /** Reflow OCR tokens into lines using their bounding boxes (mirrors backend reconstruct_text). */
  private reconstructPageText(ocr: any): string {
    const texts: string[] = ocr?.rec_texts || [];
    const boxes: number[][][] = ocr?.rec_boxes || [];
    if (texts.length === 0) return '';
    if (boxes.length !== texts.length) return texts.join('\n');

    const tokens = texts.map((text, i) => {
      const box = boxes[i] || [];
      const ys = box.map(pt => pt[1]);
      const xs = box.map(pt => pt[0]);
      const yc = ys.length ? ys.reduce((a, b) => a + b, 0) / ys.length : 0;
      const h = ys.length ? Math.max(...ys) - Math.min(...ys) : 0;
      const x = xs.length ? Math.min(...xs) : 0;
      return { text, yc, x, h };
    });

    tokens.sort((a, b) => a.yc - b.yc || a.x - b.x);

    const lines: { text: string; x: number }[][] = [];
    let current = [tokens[0]];
    for (const tok of tokens.slice(1)) {
      const ref = current[current.length - 1];
      const tol = Math.max(ref.h, tok.h, 1) * 0.6;
      if (Math.abs(tok.yc - ref.yc) <= tol) {
        current.push(tok);
      } else {
        lines.push(current);
        current = [tok];
      }
    }
    lines.push(current);

    return lines
      .map(line => line.sort((a, b) => a.x - b.x).map(t => t.text).join(' '))
      .join('\n');
  }

  /** Mark all selected documents as reviewed. */
  markSelectedReviewed(): void {
    if (!this.hasSelection) {
      alert(this.getTranslation('uploadDocs.pleaseSelectDocument'));
      return;
    }
    const oids = Array.from(this.selectedDocs);
    let completed = 0;
    for (const oid of oids) {
      const doc = this.documents.find(d => d.oid === oid);
      if (!doc || !this.canMarkReviewed(doc)) {
        completed++;
        continue;
      }
      this.dossierService.markReviewed(this.dossierId, this.uploadId, doc.oid).subscribe({
        next: () => {
          this.docExtractionStatus[doc.oid] = 'reviewed';
          this.refreshSingleDocument(doc.oid);
          completed++;
          if (completed === oids.length) { this.selectedDocs.clear(); }
        },
        error: (err) => {
          this.error = err?.error?.detail || err?.message || 'Failed to mark as reviewed';
          completed++;
        },
      });
    }
  }

  /** Helper to get a translation string synchronously (for alert messages). */
  private getTranslation(key: string): string {
    return this.translate.instant(key);
  }

  // --- Page reorder controls (AJAX — targeted update) ---

  movePageFirst(doc: Document, page: Page): void {
    this.applyPageOperation(
      this.dossierService.reorderPage(this.dossierId, this.uploadId, doc.oid, page.oid, 1),
      doc.oid
    );
  }

  movePageLeft(doc: Document, page: Page): void {
    this.applyPageOperation(
      this.dossierService.reorderPage(this.dossierId, this.uploadId, doc.oid, page.oid, Math.max(1, page.page_number - 1)),
      doc.oid
    );
  }

  movePageRight(doc: Document, page: Page): void {
    const pages = this.getPagesForDocument(doc);
    this.applyPageOperation(
      this.dossierService.reorderPage(this.dossierId, this.uploadId, doc.oid, page.oid, Math.min(pages.length, page.page_number + 1)),
      doc.oid
    );
  }

  movePageLast(doc: Document, page: Page): void {
    const pages = this.getPagesForDocument(doc);
    this.applyPageOperation(
      this.dossierService.reorderPage(this.dossierId, this.uploadId, doc.oid, page.oid, pages.length),
      doc.oid
    );
  }

  // --- Page move between documents ---

  movePageUp(doc: Document, page: Page): void {
    this.applyPageOperation(
      this.dossierService.movePage(this.dossierId, this.uploadId, doc.oid, page.oid, 'up')
    );
  }

  movePageDown(doc: Document, page: Page): void {
    this.applyPageOperation(
      this.dossierService.movePage(this.dossierId, this.uploadId, doc.oid, page.oid, 'down')
    );
  }

  // --- Split & merge ---

  splitAtPage(doc: Document, page: Page): void {
    this.applyPageOperation(
      this.dossierService.splitDocument(this.dossierId, this.uploadId, doc.oid, page.oid)
    );
  }

  mergeWithNext(doc: Document): void {
    this.applyPageOperation(
      this.dossierService.mergeDocumentNext(this.dossierId, this.uploadId, doc.oid)
    );
  }

  // --- Thumbnail drag & drop (within same document) ---

  onThumbDragStart(event: DragEvent, doc: Document, page: Page): void {
    if (!this.canModifyDocuments) { event.preventDefault(); return; }
    this.dragSource = { docOid: doc.oid, pageOid: page.oid };
    event.dataTransfer!.effectAllowed = 'move';
    event.dataTransfer!.setData('text/plain', page.oid);
  }

  onThumbDragEnd(): void {
    this.dragSource = null;
    this.dragOverTarget = null;
  }

  onThumbDragOver(event: DragEvent, doc: Document): void {
    if (!this.dragSource) { return; }
    // Allow drop only within the same document
    if (this.dragSource.docOid !== doc.oid) { return; }
    event.preventDefault();
    event.dataTransfer!.dropEffect = 'move';
  }

  onThumbDragOverItem(event: DragEvent, doc: Document, page: Page): void {
    if (!this.dragSource || this.dragSource.docOid !== doc.oid) { return; }
    if (this.dragSource.pageOid === page.oid) { return; }
    event.preventDefault();
    event.stopPropagation();
    this.dragOverTarget = { docOid: doc.oid, pageOid: page.oid };
  }

  onThumbDragLeaveItem(): void {
    this.dragOverTarget = null;
  }

  onThumbDrop(event: DragEvent, doc: Document): void {
    event.preventDefault();
    if (!this.dragSource || this.dragSource.docOid !== doc.oid || !this.dragOverTarget) {
      this.dragSource = null;
      this.dragOverTarget = null;
      return;
    }

    const pages = this.getPagesForDocument(doc);
    const targetPage = pages.find(p => p.oid === this.dragOverTarget!.pageOid);
    if (!targetPage) { this.dragSource = null; this.dragOverTarget = null; return; }

    const newPosition = targetPage.page_number;
    this.dragSource = null;
    this.dragOverTarget = null;

    const sourcePageOid = event.dataTransfer!.getData('text/plain');
    this.applyPageOperation(
      this.dossierService.reorderPage(this.dossierId, this.uploadId, doc.oid, sourcePageOid, newPosition),
      doc.oid
    );
  }

  // --- Thumbnail click → side panel viewer ---

  openPageInViewer(page: Page): void {
    this.viewerPage = page;
    this.viewerImageUrl = this.pageImageUrls[page.oid] || null;
  }

  closeViewer(): void {
    this.viewerPage = null;
    this.viewerImageUrl = null;
  }

  // --- Resizable panel ---

  onResizeStart(event: MouseEvent): void {
    event.preventDefault();
    this.resizing = true;
    this.resizeStartX = event.clientX;
    this.resizeStartWidth = this.viewerPanelWidth;

    const onMouseMove = (e: MouseEvent) => {
      if (!this.resizing) return;
      // Dragging left increases panel width (panel is on the right)
      const delta = this.resizeStartX - e.clientX;
      this.viewerPanelWidth = Math.max(200, Math.min(800, this.resizeStartWidth + delta));
    };

    const onMouseUp = () => {
      this.resizing = false;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }

  // --- Delete document ---

  deleteDocument(doc: Document): void {
    // Offer the reversible choice: merge into previous (corrects over-split) or
    // ignore the pages (kept, but excluded from extraction/examination).
    const merge = confirm(
      'Delete this document group?\n\n' +
      'OK = merge its pages into the previous document (reversible via split).\n' +
      'Cancel = choose to ignore these pages instead.'
    );
    if (merge) {
      this.applyDeleteMode(doc, 'merge');
      return;
    }
    if (confirm('Ignore these pages instead? They are kept but excluded from extraction and examination (reversible by re-classifying the document).')) {
      this.applyDeleteMode(doc, 'ignore');
    }
  }

  private applyDeleteMode(doc: Document, mode: 'merge' | 'ignore'): void {
    this.dossierService.deleteDocument(this.dossierId, this.uploadId, doc.oid, mode).subscribe({
      next: () => { this.loadDocuments(); },
      error: (err) => { this.error = err?.error?.detail || err?.message || 'Failed to delete document'; }
    });
  }

  /** Ignore a whole document (group of pages) — excluded from extraction/examination, reversible. */
  ignoreDocument(doc: Document): void {
    if (!confirm('Ignore this document? Its pages are kept but excluded from extraction and examination. Reversible by re-classifying the document.')) {
      return;
    }
    this.dossierService.ignoreDocument(this.dossierId, this.uploadId, doc.oid).subscribe({
      next: () => { this.loadDocuments(); },
      error: (err) => { this.error = err?.error?.detail || err?.message || 'Failed to ignore document'; }
    });
  }

  /** Toggle a single page's ignored state (one click, reversible). */
  togglePageIgnored(doc: Document, page: Page): void {
    const ignored = page.status !== PageStatus.IGNORED;
    this.dossierService.ignorePage(this.dossierId, this.uploadId, doc.oid, page.oid, ignored).subscribe({
      next: () => { this.loadDocuments(); },
      error: (err) => { this.error = err?.error?.detail || err?.message || 'Failed to update page'; }
    });
  }

  // --- Extraction ---

  /** Check that every document has a document type assigned before extraction. */
  get allDocumentsTyped(): boolean {
    return this.documents.every(d => !!d.document_type);
  }

  triggerExtraction(): void {
    if (!this.allDocumentsTyped) {
      this.error = 'All documents must have a document type assigned before extraction.';
      return;
    }
    // Re-digitize requires at least one document selected
    if (this.isExtractionDone && !this.hasSelection) {
      this.error = 'Select one or more documents to re-digitize.';
      return;
    }
    this.extracting = true;
    this.error = '';

    // If documents are selected and a model is chosen, use re-extract per document
    if (this.hasSelection && this.extractModel) {
      const selectedOids = Array.from(this.selectedDocs);
      let completed = 0;
      let failed = false;

      for (const docOid of selectedOids) {
        const doc = this.documents.find(d => d.oid === docOid);
        if (!doc) { continue; }

        this.docExtractionStatus[doc.oid] = DocumentStatus.EXTRACTING;

        this.dossierService.reExtract(
          this.dossierId, this.uploadId, doc.oid,
          this.extractModel, doc.document_type, this.outputFormat,
        ).subscribe({
          next: () => {
            this.startDocExtractionPolling(doc.oid);
            completed++;
            if (completed === selectedOids.length) {
              this.extracting = false;
              this.selectedDocs.clear();
            }
          },
          error: (err) => {
            if (!failed) {
              this.error = extractApiError(err, 'Failed to trigger re-extraction');
              failed = true;
            }
            completed++;
            if (completed === selectedOids.length) {
              this.extracting = false;
            }
          },
        });
      }
      return;
    }

    // Default: trigger extraction for all documents (first-time extraction)
    this.dossierService.triggerExtraction(this.dossierId, this.uploadId).subscribe({
      next: () => {
        this.extracting = false;
        // Mark all documents as extracting and start polling each
        for (const doc of this.documents) {
          this.docExtractionStatus[doc.oid] = DocumentStatus.EXTRACTING;
          this.startDocExtractionPolling(doc.oid);
        }
      },
      error: (err) => {
        if (err?.status === 400) {
          this.dekWarning = true;
        }
        this.error = extractApiError(err, 'Failed to trigger extraction');
        this.trackError('trigger_extraction', this.error);
        this.extracting = false;
      }
    });
  }

  get isExtractionDone(): boolean {
    return this.uploadStatus === UploadStatus.EXTRACTION_COMPLETED
      || this.uploadStatus === UploadStatus.COMPLETED;
  }

  /** True when the Extract button should be enabled. */
  get canExtract(): boolean {
    if (this.extracting || this.operationsLocked) { return false; }
    if (!this.isClassificationCompleted) { return false; }
    if (!this.allDocumentsTyped) { return false; }
    // Re-extraction (post-initial) of selected docs requires a model
    if (this.isExtractionDone && this.hasSelection) { return !!this.extractModel; }
    return true;
  }

  /** True when any document has extraction_failed status. */
  get hasFailedDocs(): boolean {
    return this.documents.some(d =>
      this.docExtractionStatus[d.oid] === DocumentStatus.EXTRACTION_FAILED
    );
  }

  /** True when all documents have been extracted (or reviewed). */
  get allDocumentsExtracted(): boolean {
    return this.documents.length > 0 && this.documents.every(d => {
      const status = this.docExtractionStatus[d.oid] || d.status;
      return status === DocumentStatus.EXTRACTED || status === DocumentStatus.REVIEWED;
    });
  }

  goToExtractionReview(): void {
    this.router.navigate([
      '/dossiers', this.dossierId,
      'uploads', this.uploadId,
      'extraction-review',
    ]);
  }

  /** Navigate to the detailed extraction review for a single document. */
  openExtractionDetail(doc: Document): void {
    this.router.navigate([
      '/dossiers', this.dossierId,
      'uploads', this.uploadId,
      'documents', doc.oid,
      'extract',
    ]);
  }

  /** Navigate to extraction detail when clicking the thumbnail grid area (not individual page controls). */
  onThumbnailGridClick(event: MouseEvent, doc: Document): void {
    // Only navigate if extraction is available and click wasn't on a button, page control, or thumbnail image
    const target = event.target as HTMLElement;
    if (target.closest('button') || target.closest('.page-controls') || target.closest('img')) return;
    const status = this.docExtractionStatus[doc.oid];
    if (status === 'extracted' || status === 'reviewed' || status === 'extraction_failed') {
      this.openExtractionDetail(doc);
    }
  }

  /** Mark a document as reviewed directly from the unified view. */
  markDocReviewed(doc: Document): void {
    this.dossierService.markReviewed(this.dossierId, this.uploadId, doc.oid).subscribe({
      next: () => {
        this.docExtractionStatus[doc.oid] = 'reviewed';
        this.refreshSingleDocument(doc.oid);
      },
      error: (err) => {
        this.error = err?.error?.detail || err?.message || 'Failed to mark as reviewed';
      },
    });
  }

  cancelExtraction(doc: Document): void {
    this.dossierService.cancelExtraction(this.dossierId, this.uploadId, doc.oid).subscribe({
      next: () => {
        this.docExtractionStatus[doc.oid] = 'extraction_failed';
        this.refreshSingleDocument(doc.oid);
      },
      error: (err) => {
        this.error = err?.error?.detail || err?.message || 'Failed to cancel extraction';
      },
    });
  }

  /** Check if a document can be marked as reviewed. */
  canMarkReviewed(doc: Document): boolean {
    const status = this.docExtractionStatus[doc.oid] || doc.status;
    return status === 'extracted';
  }

  /** Check if a document can be re-extracted (extracted, reviewed, or failed). */
  canExtractDoc(doc: Document): boolean {
    const status = this.docExtractionStatus[doc.oid] || doc.status;
    return status === 'extracted' || status === 'reviewed' || status === 'extraction_failed';
  }

  /** Re-extract a single document using the currently selected model. */
  extractSingleDoc(doc: Document): void {
    const model = this.extractModel;
    if (!model) {
      this.error = 'Select a model before re-extracting.';
      return;
    }
    this.docExtractionStatus[doc.oid] = DocumentStatus.EXTRACTING;
    this.dossierService.reExtract(
      this.dossierId, this.uploadId, doc.oid,
      model, doc.document_type, this.outputFormat,
    ).subscribe({
      next: () => this.startDocExtractionPolling(doc.oid),
      error: (err) => {
        this.error = extractApiError(err, 'Failed to re-extract document');
        this.trackError('re_extract', this.error);
        this.docExtractionStatus[doc.oid] = 'extraction_failed';
      },
    });
  }

  /** Send a single document to the org's downstream system. */
  exportDoc(doc: Document): void {
    if (this.exportingDocId === doc.oid) { return; }
    this.exportingDocId = doc.oid;
    this.exportDocMsg = { ...this.exportDocMsg, [doc.oid]: '' };
    const sysName = this.downstreamSystemName || 'destination';
    this.dossierService.exportDocumentToDownstream(
      this.dossierId, this.uploadId, doc.oid,
    ).subscribe({
      next: (res: DownstreamExportResult) => {
        const item = res.results && res.results[0];
        if (item) {
          doc.downstream_status = item.sent ? 'sent' : 'rejected';
          doc.downstream_error = item.sent ? null : (item.response || item.reason || `Rejected by ${sysName}`);
        }
        const ok = res.documents_sent === res.documents_total;
        this.exportDocMsg = {
          ...this.exportDocMsg,
          [doc.oid]: ok ? `✓ Sent to ${res.system_name || sysName}` : `✗ Cannot send to ${res.system_name || sysName}`,
        };
        this.exportingDocId = null;
      },
      error: () => {
        this.exportDocMsg = {
          ...this.exportDocMsg,
          [doc.oid]: `✗ Cannot send to ${sysName}`,
        };
        this.exportingDocId = null;
      },
    });
  }

  /** Extract all selected documents (bulk toolbar action). */
  extractSelected(): void {
    if (!this.hasSelection) { return; }
    // Delegate to existing triggerExtraction which handles selected docs
    this.triggerExtraction();
  }

  /** True when the bulk Extract Selected button should be enabled. */
  get canExtractSelected(): boolean {
    return this.hasSelection && !!this.extractModel && !this.extracting && !this.operationsLocked;
  }

  /** Check if a document is already reviewed. */
  isReviewed(doc: Document): boolean {
    const status = this.docExtractionStatus[doc.oid] || doc.status;
    return status === 'reviewed';
  }

  goBack(): void {
    this.router.navigate(['/dossiers', this.dossierId]);
  }

  // --- Extraction error helpers ---

  /** Get the structured error info from a failed extraction, if available. */
  getExtractionError(doc: Document): { error_code: string; error_detail: string; error_model: string } | null {
    return doc.extraction_error ?? null;
  }

  /** True if the document has a context_overflow error. */
  isContextOverflow(doc: Document): boolean {
    const err = this.getExtractionError(doc);
    return err?.error_code === 'context_overflow';
  }

  // --- AJAX page operation helper (targeted update, no full reload) ---

  /**
   * Execute a page operation and apply the returned document list
   * to update only the affected documents in-place.
   * @param obs The API call observable returning updated Document[]
   * @param singleDocOid If set, only update this document (for within-doc reorders)
   */
  private applyPageOperation(obs: Observable<Document[]>, singleDocOid?: string): void {
    this.error = '';
    obs.subscribe({
      next: (updatedDocs) => {
        this.applyDocumentUpdates(updatedDocs, singleDocOid);
      },
      error: (err) => { this.error = err?.error?.detail || err?.message || 'Operation failed'; }
    });
  }

  /**
   * Merge updated documents into the local array without full reload.
   * Preserves scroll position and only re-renders affected cards.
   */
  private applyDocumentUpdates(updatedDocs: Document[], singleDocOid?: string): void {
    // Build a map of updated docs by oid
    const updatedMap = new Map<string, Document>();
    for (const doc of updatedDocs) {
      updatedMap.set(doc.oid, doc);
    }

    if (singleDocOid && updatedMap.has(singleDocOid)) {
      // Single-document reorder: just update that one
      const idx = this.documents.findIndex(d => d.oid === singleDocOid);
      if (idx >= 0) {
        const updated = updatedMap.get(singleDocOid)!;
        this.documents[idx] = updated;
        this.docExtractionStatus[singleDocOid] = updated.status;
        if (updated.pages?.length) {
          this.documentPages[singleDocOid] = updated.pages;
          for (const page of updated.pages) { this.loadPageImage(page); }
        }
      }
    } else {
      // Multi-document operation (move, split, merge): replace full list
      // but preserve image URLs that haven't changed
      this.documents = updatedDocs;
      // Clean stale documentPages entries for removed documents
      const currentOids = new Set(updatedDocs.map(d => d.oid));
      for (const oid of Object.keys(this.documentPages)) {
        if (!currentOids.has(oid)) { delete this.documentPages[oid]; }
      }
      for (const doc of updatedDocs) {
        this.docExtractionStatus[doc.oid] = doc.status;
        if (doc.pages?.length) {
          this.documentPages[doc.oid] = doc.pages;
          for (const page of doc.pages) { this.loadPageImage(page); }
        }
        if (!this.docSchemaSource[doc.oid]) {
          this.docSchemaSource[doc.oid] = doc.document_type?.startsWith('xsd/') ? 'external' : 'internal';
        }
      }
    }
  }

  // --- Helpers ---

  get isClassificationCompleted(): boolean {
    return this.uploadStatus === UploadStatus.CLASSIFICATION_COMPLETED
      || this.uploadStatus === UploadStatus.CLASSIFICATION_FAILED
      || this.uploadStatus === UploadStatus.EXTRACTING
      || this.uploadStatus === UploadStatus.EXTRACTION_COMPLETED
      || this.uploadStatus === UploadStatus.COMPLETED;
  }

  get isProcessing(): boolean {
    return !!this.uploadStatus && !this.isClassificationCompleted && !this.isFailedStatus;
  }

  get isFailedStatus(): boolean {
    const failed: string[] = [
      UploadStatus.PROCESSING_FAILED,
      UploadStatus.OCR_FAILED,
      UploadStatus.CLASSIFICATION_FAILED,
      UploadStatus.EXTRACTION_FAILED,
    ];
    return failed.includes(this.uploadStatus as string);
  }

  get processingStageLabel(): string {
    switch (this.uploadStatus) {
      case UploadStatus.UPLOADED:       return 'Uploaded — waiting for processing';
      case UploadStatus.PROCESSING:     return 'Processing archive / PDF...';
      case UploadStatus.OCR_COMPLETED:  return 'OCR completed — classifying documents...';
      default:                          return this.uploadStatus as string;
    }
  }

  isFirstDocument(doc: Document): boolean {
    return this.documents.length > 0 && this.documents[0].oid === doc.oid;
  }

  isLastDocument(doc: Document): boolean {
    return this.documents.length > 0 && this.documents[this.documents.length - 1].oid === doc.oid;
  }

  getPagesForDocument(doc: Document): Page[] {
    return this.documentPages[doc.oid] || [];
  }

  getDocStatusLabel(doc: Document): string {
    return this.docExtractionStatus[doc.oid] || doc.status || '';
  }

  getDocTokens(doc: Document): number | null {
    return this.docExtractionTokens[doc.oid] ?? null;
  }

  /** Send error event to GA4 for dossier flow failures. */
  private trackError(step: string, message: string): void {
    if (typeof gtag === 'function') {
      gtag('event', 'dd_error', {
        event_category: 'dossier_flow',
        step,
        error_message: (message || '').substring(0, 100),
      });
    }
  }
}
